"use client"

import { useEffect, useState } from "react"

import { hashKey, keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { useParams } from "next/navigation"

import type { SearchItemsOptions } from "@/lib/services/search/search"
import { searchItems } from "@/lib/services/search/search.client"
import type { SearchPageData, SearchQuery } from "@/lib/types/search"
import { searchGateway } from "@/platform/api/gateways/search"
import { useBrowseReadiness, useBrowseValue } from "@/platform/app-mode/use-browse-value"
import { useScreenPreparation } from "@/platform/app-mode/use-screen-visible"
import { useGateway } from "@/platform/session/use-api"
import { useViewer } from "@/platform/session/use-viewer"
import { privateKey, UNRESOLVED_VIEWER } from "@/platform/session/viewer"
import { onlyForViewer } from "@/platform/session/viewer-boundary"

const useSearchScope = () => {
  const viewer = useViewer()
  const { locale, countryCode } = useParams<{ locale?: string; countryCode?: string }>() ?? {}
  return {
    viewer,
    key: (...parts: readonly unknown[]) => privateKey(viewer ?? UNRESOLVED_VIEWER, "search", locale, countryCode, ...parts),
    meta: viewer === null ? undefined : { viewer },
    keepPrevious: onlyForViewer(viewer, keepPreviousData),
  }
}

export const useSearchData = (query: SearchQuery, page: number = 1, options?: { enabled?: boolean }) => {
  const { enabled = true } = options ?? {}
  const search = useGateway(searchGateway)
  const scope = useSearchScope()

  // Every caller is a typeahead, so each keystroke past the debounce is a new key.
  // Without keepPreviousData the suggestion list empties for the length of the
  // request and the panel collapses to a one-line "Buscando…" before reopening.
  return useQuery({
    queryKey: scope.key("page", query, page),
    meta: scope.meta,
    queryFn: ({ signal }) => searchItems(search, query, page, undefined, signal),
    enabled,
    placeholderData: scope.keepPrevious,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })
}

// initialPage (server-fetched page 1) seeds the query so the first page renders in
// the initial HTML and hydrates without a refetch/pop-in.
interface InfiniteSearchOptions extends SearchItemsOptions {
  keepPreviousResults?: boolean
  renderedQuery?: string
  startPage?: number
}

const useInitialPageFor = (query: SearchQuery, viewer: string | null, initialPage?: SearchPageData): SearchPageData | undefined => {
  const [seed, setSeed] = useState({ page: initialPage, query, viewer })
  const arrivedWithThisRender = seed.page !== initialPage
  if (arrivedWithThisRender) setSeed({ page: initialPage, query, viewer })
  else if (seed.viewer === null && viewer !== null) setSeed({ ...seed, viewer })
  const seedQuery = arrivedWithThisRender ? query : seed.query
  const seedViewer = arrivedWithThisRender ? viewer : seed.viewer
  const sameViewer = seedViewer === null || viewer === null || seedViewer === viewer
  return sameViewer && hashKey([seedQuery]) === hashKey([query]) ? initialPage : undefined
}

export const useInfiniteSearchData = (query: SearchQuery, initialPage?: SearchPageData, options?: InfiniteSearchOptions) => {
  const includeFacets = options?.includeFacets ?? false
  const startPage = options?.startPage ?? 1
  const scope = useSearchScope()
  const seed = useInitialPageFor(query, scope.viewer, initialPage)
  const seededPage = seed?.pageNumber === startPage ? seed : undefined
  const search = useGateway(searchGateway)
  const result = useInfiniteQuery({
    queryKey: scope.key("infinite", query, includeFacets, startPage),
    meta: scope.meta,
    queryFn: ({ pageParam, signal }: { pageParam: number; signal: AbortSignal }) =>
      searchItems(search, query, pageParam, { includeFacets: includeFacets && pageParam === startPage }, signal),
    getNextPageParam: (lastPage) => {
      if (lastPage.isEmpty || !lastPage.hasMorePages) {
        return undefined
      }
      return lastPage.pageNumber + 1
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageNumber <= startPage) {
        return undefined
      }
      return firstPage.pageNumber - 1
    },
    initialPageParam: startPage,
    initialData: seededPage ? { pages: [seededPage], pageParams: [startPage] } : undefined,
    placeholderData: options?.keepPreviousResults ? scope.keepPrevious : undefined,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })
  const preparing = useScreenPreparation()
  const { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage } = result
  const pages = result.isPlaceholderData ? 0 : (result.data?.pages.length ?? 0)
  const browseScope = `listing:${hashKey([query, includeFacets, startPage])}`
  const [horizon, setHorizon, entryId] = useBrowseValue(browseScope, pages, options?.renderedQuery)
  useEffect(() => {
    if (pages > horizon) setHorizon(pages)
    if (preparing && pages > 0 && pages < horizon && hasNextPage && !isFetching && !isFetchNextPageError) void fetchNextPage()
  }, [pages, horizon, preparing, hasNextPage, isFetching, isFetchNextPageError, fetchNextPage, setHorizon])
  useBrowseReadiness(!result.isPlaceholderData && (pages >= horizon || result.isError || (pages > 0 && !result.hasNextPage)), entryId)
  return result
}
